"use client";

import type {
  CellValueChangedEvent,
  GetRowIdParams,
  ICellRendererParams,
  RowClassParams,
} from "ag-grid-community";
import Papa from "papaparse";
import { useMemo } from "react";

import { DataTable } from "../data-table/DataTable";
import type { SimpleTableColumn } from "../data-table/SimpleTable/types";

const ROW_KEY = "__rowKey";

interface CsvRow {
  [ROW_KEY]: number;
  id?: string | number;
  [key: string]: unknown;
}

const DATA_URL_PREFIX = "data:";
const BASE64_MARKER = ";base64,";

const decodeCsvContent = (content: string): string => {
  if (
    !content.startsWith(DATA_URL_PREFIX) ||
    !content.includes(BASE64_MARKER)
  ) {
    return content;
  }

  const base64Content = content.slice(
    content.indexOf(BASE64_MARKER) + BASE64_MARKER.length,
  );
  const binary = atob(base64Content);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
};

const decodedLooksLikeSvgOrXml = (text: string): boolean => {
  const t = text.trimStart();
  return (
    t.startsWith("<svg") || t.startsWith("<?xml") || t.startsWith("<!DOCTYPE")
  );
};

const buildCsvPreviewData = (content: string) => {
  const decoded = decodeCsvContent(content);
  if (decodedLooksLikeSvgOrXml(decoded)) {
    return {
      headers: [] as string[],
      rows: [] as CsvRow[],
      error: "Decoded content looks like XML or SVG, not CSV.",
    };
  }

  const result = Papa.parse<Record<string, string>>(decoded, {
    header: true,
    skipEmptyLines: true,
  });

  const headers = result.meta.fields ?? [];
  const rows: CsvRow[] = result.data.map((row, index) => ({
    ...Object.fromEntries(headers.map((header) => [header, row[header] ?? ""])),
    [ROW_KEY]: index,
  }));

  return {
    headers,
    rows,
    error: result.errors[0]?.message ?? null,
  };
};

type DiffStatus = "unchanged" | "modified" | "added" | "removed";

interface CsvDiffRow extends CsvRow {
  _diffStatus: DiffStatus;
  _originalValues?: Record<string, unknown>;
}

const ROW_ADDED_BG = "rgb(236 253 245)";
const ROW_REMOVED_BG = "rgb(254 242 242)";

const buildUnifiedDiffRows = (
  currentRows: CsvRow[],
  originalRows: CsvRow[],
  headers: string[],
): CsvDiffRow[] => {
  const maxLen = Math.max(currentRows.length, originalRows.length);
  const merged: CsvDiffRow[] = [];

  for (let index = 0; index < maxLen; index += 1) {
    const current = currentRows[index];
    const original = originalRows[index];

    if (current && original) {
      const changed = headers.some(
        (header) =>
          String(current[header] ?? "") !== String(original[header] ?? ""),
      );
      merged.push({
        ...current,
        [ROW_KEY]: merged.length,
        _diffStatus: changed ? "modified" : "unchanged",
        _originalValues: changed
          ? Object.fromEntries(
              headers.map((header) => [header, original[header] ?? ""]),
            )
          : undefined,
      });
    } else if (current) {
      merged.push({
        ...current,
        [ROW_KEY]: merged.length,
        _diffStatus: "added",
      });
    } else if (original) {
      merged.push({
        ...original,
        [ROW_KEY]: merged.length,
        _diffStatus: "removed",
      });
    }
  }

  return merged;
};

const DiffCellRenderer = ({
  value,
  data,
  colDef,
}: ICellRendererParams<CsvRow>): React.ReactNode => {
  const displayValue =
    value === undefined || value === null ? "" : String(value);
  const diffData = data as CsvDiffRow | undefined;

  if (!diffData) {
    return displayValue;
  }

  if (diffData._diffStatus === "removed") {
    return <span className="text-rose-700 line-through">{displayValue}</span>;
  }

  if (diffData._diffStatus === "added") {
    return <span className="text-emerald-700">{displayValue}</span>;
  }

  if (diffData._diffStatus === "modified") {
    const fieldKey = typeof colDef?.field === "string" ? colDef.field : null;
    const originalValue = fieldKey
      ? (diffData._originalValues?.[fieldKey] ?? "")
      : "";
    if (fieldKey && String(originalValue) !== displayValue) {
      return (
        <span className="flex min-w-0 items-center gap-1.5 truncate">
          <span className="truncate rounded-sm bg-rose-100 px-1 text-rose-800 line-through">
            {String(originalValue) || "\u00A0"}
          </span>
          <span className="shrink-0 text-gray-400">→</span>
          <span className="truncate rounded-sm bg-emerald-100 px-1 text-emerald-800">
            {displayValue || "\u00A0"}
          </span>
        </span>
      );
    }
  }

  return displayValue;
};

export const CsvSkillFilePreview = ({
  content,
  compact = false,
  editable = false,
  onContentChange,
  originalContent,
  licenseKey,
}: {
  content: string;
  compact?: boolean;
  editable?: boolean;
  onContentChange?: (content: string) => void;
  originalContent?: string;
  licenseKey?: string;
}) => {
  const { headers, rows, error } = useMemo(
    () => buildCsvPreviewData(content),
    [content],
  );

  const diffContext = useMemo(() => {
    if (originalContent === undefined || originalContent === content) {
      return null;
    }
    const original = buildCsvPreviewData(originalContent);
    const unifiedHeaders =
      headers.length > 0
        ? [...headers, ...original.headers.filter((h) => !headers.includes(h))]
        : original.headers;
    const diffRows = buildUnifiedDiffRows(rows, original.rows, unifiedHeaders);
    return { diffRows, headers: unifiedHeaders };
  }, [originalContent, content, headers, rows]);

  const effectiveRows = diffContext?.diffRows ?? rows;
  const effectiveHeaders = diffContext?.headers ?? headers;
  const isDiffMode = diffContext !== null;

  const columns = useMemo<SimpleTableColumn<CsvRow>[]>(
    () =>
      effectiveHeaders.map((header, index) => ({
        id: index + 1,
        name: header,
        field: header,
        header,
        sortable: false,
        minWidth: 180,
        editable: editable && !isDiffMode,
        cellRenderer: isDiffMode ? DiffCellRenderer : undefined,
      })),
    [editable, effectiveHeaders, isDiffMode],
  );

  const getRowStyle = useMemo(() => {
    if (!isDiffMode) {
      return undefined;
    }
    return (params: RowClassParams<CsvRow>) => {
      const status = (params.data as CsvDiffRow | undefined)?._diffStatus;
      if (status === "added") {
        return { backgroundColor: ROW_ADDED_BG };
      }
      if (status === "removed") {
        return { backgroundColor: ROW_REMOVED_BG };
      }
      return undefined;
    };
  }, [isDiffMode]);

  const handleCellValueChanged = (event: CellValueChangedEvent<CsvRow>) => {
    if (!editable || !onContentChange || isDiffMode) {
      return;
    }

    const updatedRows = rows.map((row) =>
      row[ROW_KEY] === event.data[ROW_KEY] ? event.data : row,
    );
    const csvRows = updatedRows.map((row) =>
      Object.fromEntries(
        headers.map((header) => [header, String(row[header] ?? "")]),
      ),
    );
    onContentChange(
      Papa.unparse(csvRows, {
        columns: headers,
        header: true,
      }),
    );
  };

  const getRowId = useMemo(
    () => (params: GetRowIdParams<CsvRow>) => String(params.data[ROW_KEY]),
    [],
  );

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50 p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full min-h-0 flex-col bg-gray-50 ${compact ? "p-3" : "p-4"}`}
    >
      <div className={compact ? "h-[320px] min-h-[320px]" : "min-h-0 flex-1"}>
        <DataTable<CsvRow>
          data={effectiveRows}
          columns={columns}
          isLoading={false}
          emptyStateMessage="No CSV rows found"
          maxHeight={compact ? "320px" : undefined}
          onCellValueChanged={handleCellValueChanged}
          getRowStyle={getRowStyle}
          getRowId={getRowId}
          licenseKey={licenseKey}
          gridOptions={
            editable && !isDiffMode
              ? {
                  singleClickEdit: true,
                  stopEditingWhenCellsLoseFocus: true,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};
